export interface TossCardCheckoutParams {
    clientKey: string;
    amount: number;
    /** 가맹점 주문번호 — Toss에 전달되는 문자열 */
    tossMerchantOrderId: string;
    orderName: string;
    customerName: string;
    /** 구매자 이메일 — 토스 결제창/영수증 이메일 발송에 사용 (없으면 전달하지 않음) */
    customerEmail?: string;
    /** 결제 통화 (기본값: KRW) */
    currency?: string;
}

/**
 * 토스 일반결제(카드) — 리다이렉트 방식
 * @tosspayments/payment-sdk v1: requestPayment(결제수단, 옵션)
 */
export async function requestTossCardCheckout(params: TossCardCheckoutParams): Promise<void> {
    const { loadTossPayments } = await import('@tosspayments/payment-sdk');
    const tossPayments = await loadTossPayments(params.clientKey);

    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const successUrl = `${origin}/pay/toss/success`;
    const failUrl = `${origin}/pay/toss/fail`;

    const toss = tossPayments as unknown as {
        requestPayment: (
            method: string,
            opts: Record<string, unknown>
        ) => Promise<void> | void;
    };

    const customerEmail = params.customerEmail?.trim();

    // TODO(임시 디버깅): 이메일 전달 확인용 — 확인 후 제거
    console.log('[toss-debug] requestTossCardCheckout', {
        rawCustomerEmail: params.customerEmail,
        customerEmail,
        willSendCustomerEmail: Boolean(customerEmail),
    });

    await toss.requestPayment('카드', {
        amount: params.amount,
        currency: params.currency || 'KRW',
        orderId: params.tossMerchantOrderId,
        orderName: params.orderName,
        customerName: params.customerName,
        ...(customerEmail ? { customerEmail } : {}),
        successUrl,
        failUrl,
        easyPay: null, // 간편결제 비활성화
    });
}

/** 토스 가맹점 주문번호: 영문·숫자·`-`·`_` 만, 6~64자 */
export function isValidTossMerchantOrderId(s: string): boolean {
    const t = s.trim();
    return t.length >= 6 && t.length <= 64 && /^[A-Za-z0-9_-]+$/.test(t);
}

/**
 * 학생 오픈클래스: `POST /order/open-class` 응답 `orderNumber`를 토스에 그대로 쓴다(토스 규칙 충족 시).
 * 백엔드가 짧거나 특수문자면 `ORD-{id}-{suffix}` 로 대체.
 */
export function tossMerchantOrderIdFromOpenClassOrderNumber(
    orderNumber: string | undefined,
    medicrewOrderId: number
): string {
    const t = orderNumber?.trim() ?? '';
    if (isValidTossMerchantOrderId(t)) return t;
    return buildTossMerchantOrderId(medicrewOrderId);
}

/**
 * 오픈클래스 외(기관 주문 등) — 숫자 PK만 있을 때 토스 규칙 만족하는 가맹점 주문번호 생성.
 */
export function buildTossMerchantOrderId(medicrewOrderId: number): string {
    const id = Math.floor(Number(medicrewOrderId));
    const suffix =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID().replace(/-/g, '').slice(0, 8)
            : String(Date.now());
    return `ORD-${id}-${suffix}`;
}

/**
 * 토스 successUrl 쿼리 `orderId` → Medicrew 주문 PK.
 * `ORD-{id}-…` · 예전 `MCW-{id}-…` · 숫자만(레거시) 호환.
 */
export function parseMedicrewOrderIdFromTossOrderId(tossOrderId: string): number | null {
    const s = String(tossOrderId).trim();
    for (const re of [/^ORD-(\d+)-/i, /^MCW-(\d+)-/i]) {
        const m = re.exec(s);
        if (m) {
            const n = parseInt(m[1], 10);
            return Number.isFinite(n) && n > 0 ? n : null;
        }
    }
    if (/^\d+$/.test(s)) {
        const n = parseInt(s, 10);
        return Number.isFinite(n) && n > 0 ? n : null;
    }
    return null;
}
